import React, { useState, useEffect } from 'react';
import './POSOpenSalesModal.css';
import apiService from '../../services/api';
import dateUtils from '../../utils/dateUtils';
import { useBusinessSettings } from '../../contexts/BusinessSettingsContext';

const POSOpenSalesModal = ({ onClose, defaultDate, onEdit, onDelete }) => {
  const { formatCurrency } = useBusinessSettings();
  const [sales, setSales] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [selectedSale, setSelectedSale] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Filters
  const [date, setDate] = useState(defaultDate || '');
  const [customer, setCustomer] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [invoiceNo, setInvoiceNo] = useState('');
  
  useEffect(() => {
    loadSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getContactDisplayName = (contact, fallback = 'Walk-in Customer') => {
    if (!contact) return fallback;
    const bizName = contact.supplier_business_name || contact.business_name || '';
    const indName = contact.name || contact.first_name || '';
    if (bizName && indName && indName !== 'Walk-in Customer' && indName !== 'Walk-In Customer') {
      return `${bizName}, ${indName}`;
    }
    if (bizName) return bizName;
    if (indName) return indName;
    return fallback;
  };

  const loadSales = async () => {
    setIsLoading(true);
    try {
      const params = { 
        per_page: -1,
        is_direct_sale: 0,
      };
      
      const res = await apiService.getSales(params);
      const data = res.data || res || [];
      const salesArray = Array.isArray(data) ? data : (data.data || []);
      
      // Transform safely with complete product & payment details
      const transformed = salesArray.map(sale => {
        const rawLines = sale.sell_lines || sale.products || [];
        const items = rawLines.map(item => {
          const name = item.product?.name 
            || item.product_name 
            || item.variations?.product?.name 
            || item.variation?.product?.name
            || item.variations?.name
            || item.name 
            || (item.product_id ? `Product ID: ${item.product_id}` : 'Product');

          const qty = parseFloat(item.quantity || item.qty || 1);
          const price = parseFloat(item.unit_price_inc_tax || item.unit_price || item.unit_price_before_discount || 0);
          const lineDiscount = parseFloat(item.line_discount_amount || 0);
          const lineTotal = (price * qty) - lineDiscount;

          return {
            ...item,
            productName: name,
            quantity: qty,
            unitPrice: price,
            lineDiscount: lineDiscount,
            lineTotal: lineTotal
          };
        });

        const paid = parseFloat(
          sale.total_paid !== undefined 
            ? sale.total_paid 
            : (sale.payment_lines ? sale.payment_lines.reduce((s, p) => s + parseFloat(p.amount || 0), 0) : 0)
        );

        return {
          id: sale.id,
          invoiceNo: sale.invoice_no,
          customer: getContactDisplayName(sale.contact || sale.customer),
          dateTime: sale.transaction_date || sale.created_at,
          status: sale.status || 'final',
          paymentStatus: sale.payment_status || (paid >= parseFloat(sale.final_total || 0) ? 'paid' : 'due'),
          subTotal: parseFloat(sale.total_before_tax || sale.final_total || 0),
          tax: parseFloat(sale.tax_amount || 0),
          discount: parseFloat(sale.discount_amount || 0),
          total: parseFloat(sale.final_total || 0),
          paidAmount: paid,
          items: items,
          apiData: sale
        };
      });
      
      setSales(transformed);
      filterSales(transformed, date, customer, invoiceNo, statusFilter);
    } catch (error) {
      /* console.error('Failed to load sales:', error); */
    }
    setIsLoading(false);
  };
  
  const filterSales = (list, d, c, inv, st) => {
    let result = list;

    if (d && d.trim()) {
      const cleanD = d.trim();
      result = result.filter(s => {
        if (!s.dateTime) return false;
        const isoDate = s.dateTime.substring(0, 10);
        const formattedDate = dateUtils.formatDate(s.dateTime);
        const reversedD = cleanD.includes('-') ? cleanD.split('-').reverse().join('-') : cleanD;
        return (
          s.dateTime.includes(cleanD) || 
          isoDate === cleanD || 
          isoDate === reversedD || 
          formattedDate.includes(cleanD) ||
          formattedDate.replace(/\//g, '-').includes(cleanD)
        );
      });
    }

    if (c) {
      result = result.filter(s => s.customer.toLowerCase().includes(c.toLowerCase()));
    }

    if (inv) {
      result = result.filter(s => s.invoiceNo && s.invoiceNo.toLowerCase().includes(inv.toLowerCase()));
    }

    if (st && st !== 'All') {
      if (st === 'Completed') {
        result = result.filter(s => s.status === 'final');
      } else if (st === 'Pending') {
        result = result.filter(s => s.paymentStatus === 'due' || s.status === 'pending');
      } else if (st === 'Draft') {
        result = result.filter(s => s.status === 'draft');
      }
    }

    setFilteredSales(result);

    if (result.length > 0) {
      setSelectedSale(result[0]);
    } else {
      setSelectedSale(null);
    }
  };
  
  const handleSearch = () => {
    filterSales(sales, date, customer, invoiceNo, statusFilter);
  };
  
  const handlePrint = (sale) => {
    if (!sale) return;
    const invoiceUrl = sale.apiData?.invoice_url || sale.invoiceUrl;
    if (invoiceUrl) {
      window.open(invoiceUrl, '_blank');
    } else {
      alert('Invoice URL not available for this sale.');
    }
  };

  const handleDelete = async (sale) => {
    if (!window.confirm(`Are you sure you want to delete invoice ${sale.invoiceNo}?`)) return;
    try {
      await apiService.deleteSale(sale.id);
      if (onDelete) onDelete(sale.id);
      loadSales();
    } catch (error) {
      alert(`Failed to delete sale: ${error.message}`);
    }
  };

  return (
    <div className="pos-open-sales-modal" onClick={onClose}>
      <div className="pos-osm-content" onClick={e => e.stopPropagation()}>
        <div className="pos-osm-header">
          <h2>Sales</h2>
          <button className="pos-osm-close" onClick={onClose}>×</button>
        </div>
        
        <div className="pos-osm-filters">
          <input 
            type="date" 
            className="pos-osm-input" 
            value={date} 
            onChange={e => { setDate(e.target.value); filterSales(sales, e.target.value, customer, invoiceNo, statusFilter); }} 
          />
          <select 
            className="pos-osm-input" 
            value={customer} 
            onChange={e => { setCustomer(e.target.value); filterSales(sales, date, e.target.value, invoiceNo, statusFilter); }}
          >
            <option value="">Customer</option>
            {[...new Set(sales.map(s => s.customer))].filter(Boolean).map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select 
            className="pos-osm-input"
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); filterSales(sales, date, customer, invoiceNo, e.target.value); }}
          >
            <option value="All">All Sales</option>
            <option value="Completed">Completed</option>
            <option value="Pending">Pending</option>
            <option value="Draft">Draft</option>
          </select>
          <input 
            type="text" 
            className="pos-osm-input" 
            placeholder="Invoice No" 
            value={invoiceNo} 
            onChange={e => { setInvoiceNo(e.target.value); filterSales(sales, date, customer, e.target.value, statusFilter); }} 
          />
          <div style={{ flexBasis: '100%', display: 'flex' }}>
            <button className="pos-osm-search-btn" onClick={handleSearch}>Search</button>
          </div>
        </div>
        
        {isLoading ? (
          <div className="pos-osm-loading">Loading sales...</div>
        ) : (
          <div className="pos-osm-body">
            {/* LEFT TAB */}
            <div className="pos-osm-left">
              <table className="pos-osm-table">
                <thead>
                  <tr>
                    <th>Invoice No</th>
                    <th>Customer</th>
                    <th>Date Time</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSales.map(sale => (
                    <tr 
                      key={sale.id} 
                      className={selectedSale?.id === sale.id ? 'active' : ''}
                      onClick={() => setSelectedSale(sale)}
                    >
                      <td>{sale.invoiceNo}</td>
                      <td>{sale.customer}</td>
                      <td>{dateUtils.formatDateTime(sale.dateTime)}</td>
                    </tr>
                  ))}
                  {filteredSales.length === 0 && (
                     <tr><td colSpan="3" style={{textAlign: 'center', padding: '20px'}}>No sales found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* RIGHT TAB */}
            <div className="pos-osm-right">
              <div className="pos-osm-detail-title">Sale Details</div>
              
              <div className="pos-osm-detail-header">
                <div>Invoice No: {selectedSale ? selectedSale.invoiceNo : '—'}</div>
                <div style={{ textAlign: 'right' }}>Date Time: {selectedSale ? dateUtils.formatDateTime(selectedSale.dateTime) : '—'}</div>
                <div>Customer: {selectedSale ? selectedSale.customer : '—'}</div>
              </div>
              
              <div className="pos-osm-detail-items">
                <table className="pos-osm-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Price</th>
                      <th>Qty</th>
                      <th>Discount</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedSale && selectedSale.items.length > 0 ? selectedSale.items.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.productName}</td>
                        <td>{formatCurrency(item.unitPrice)}</td>
                        <td>{item.quantity}</td>
                        <td>{formatCurrency(item.lineDiscount)}</td>
                        <td>{formatCurrency(item.lineTotal)}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                          {selectedSale ? 'No items in this sale' : 'Select a sale to view items'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              <div className="pos-osm-summary">
                <div>Sub Total: {selectedSale ? formatCurrency(selectedSale.subTotal) : formatCurrency(0)}</div>
                <div>Total Item: {selectedSale ? `${selectedSale.items.reduce((s, i) => s + (i.quantity || 1), 0)} (${selectedSale.items.length})` : '0 (0)'}</div>
                <div>Tax: {selectedSale ? formatCurrency(selectedSale.tax) : formatCurrency(0)}</div>
                <div>Charge: {formatCurrency(0)}</div>
                <div>Discount: {selectedSale ? formatCurrency(selectedSale.discount) : formatCurrency(0)}</div>
                <div></div>
              </div>
              
              <div className="pos-osm-payable">
                Total Payable: {selectedSale ? formatCurrency(selectedSale.total) : formatCurrency(0)}
              </div>
              
              <div className="pos-osm-payments">
                <div>Paid Amount: {selectedSale ? formatCurrency(selectedSale.paidAmount) : formatCurrency(0)}</div>
                <div>Due Amount: {selectedSale ? formatCurrency(Math.max(0, selectedSale.total - selectedSale.paidAmount)) : formatCurrency(0)}</div>
              </div>
              
              <div className="pos-osm-footer-btns">
                <button 
                  className="pos-osm-btn pos-osm-btn--print" 
                  onClick={() => selectedSale && handlePrint(selectedSale)}
                >
                  Print Invoice
                </button>
                <button 
                  className="pos-osm-btn pos-osm-btn--edit"
                  onClick={() => selectedSale && onEdit && onEdit(selectedSale)}
                >
                  Edit
                </button>
                <button 
                  className="pos-osm-btn pos-osm-btn--delete"
                  onClick={() => selectedSale && handleDelete(selectedSale)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default POSOpenSalesModal;
